/**
 * Lazy seam for `ExecutionPlanPanel`.
 *
 * The Query Editor's Plan sub-tab is `forceMount`ed (so its local view state
 * survives tab switches), which used to drag the whole execution-plan surface —
 * tree/graph views, plan parser, export service — into the eager boot chunk even
 * for users who never open a plan. `QueryEditorTab` now renders this host, and
 * only once a plan artifact exists or the Plan sub-tab has actually been
 * selected, so the chunk is fetched on first use instead of at startup.
 *
 * Mirrors `spatial/map/SpatialMapLazy.tsx`, including its `RandomDotmSpinner`
 * fallback (repo rule — never `Loader2`).
 */

import { lazy, Suspense } from 'react';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import type { ExecutionPlanPanelProps } from './ExecutionPlanPanel';

export type { ExecutionPlanPanelProps } from './ExecutionPlanPanel';

const ExecutionPlanPanelLazy = lazy(() =>
  import('./ExecutionPlanPanel').then((m) => ({ default: m.ExecutionPlanPanel })),
);

/** Suspense-wrapped host — the ergonomic entry point for the Plan sub-tab. */
export function ExecutionPlanPanelHost(props: ExecutionPlanPanelProps) {
  return (
    <Suspense
      fallback={
        <div
          className="flex h-full w-full items-center justify-center"
          data-testid="execution-plan-panel-loading"
        >
          <RandomDotmSpinner size={24} />
        </div>
      }
    >
      <ExecutionPlanPanelLazy {...props} />
    </Suspense>
  );
}
